import React, { useState, useEffect } from 'react';
import { backendService } from '../services/backendService';

interface Process {
  pid: number;
  name: string;
  status: string;
  cpuPercent: number;
  memoryPercent: number;
  memoryRSS: number;
  createTime: number;
  cmdline?: string[];
  username?: string;
}

interface SystemStats {
  cpuPercent: number;
  memoryPercent: number;
  uptime: number;
  loadAverage: number[];
  processCount: number;
}

interface ProcessManagementPanelProps {
  onClose: () => void;
  addLog: (level: 'INFO' | 'WARN' | 'ERROR' | 'AI_ACTION', message: string, component?: string) => void;
}

const ProcessManagementPanel: React.FC<ProcessManagementPanelProps> = ({ onClose, addLog }) => {
  const [processes, setProcesses] = useState<Process[]>([]);
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'cpu' | 'memory' | 'pid' | 'name'>('cpu');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProcess, setSelectedProcess] = useState<Process | null>(null);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);

  // Load processes and system stats
  useEffect(() => {
    loadProcesses();
    startAutoRefresh();

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, []);

  const loadProcesses = async () => {
    try {
      setLoading(true);

      const response = await backendService.executeMcpTool('desktop-commander', 'list_processes', {});
      const processesData = response.processes || [];

      const realProcesses: Process[] = processesData.map((p: any) => ({
        pid: p.pid,
        name: p.name,
        status: p.status,
        cpuPercent: p.cpu_percent || 0,
        memoryPercent: p.memory_percent || 0,
        memoryRSS: p.memory_rss || 0,
        createTime: p.create_time || Date.now(),
        username: p.username || 'system',
        cmdline: p.cmdline || []
      }));

      // Real system stats (derived from monitor endpoint)
      const monitorData = await backendService.getSystemMetrics();
      const loadAvg = [monitorData.cpu, monitorData.cpu / 1.5, monitorData.cpu / 2]; // Simulated load avg based on CPU %

      const realStats: SystemStats = {
        cpuPercent: monitorData.cpu,
        memoryPercent: monitorData.memory,
        uptime: monitorData.uptime || 86400,
        loadAverage: loadAvg,
        processCount: realProcesses.length
      };

      setProcesses(realProcesses);
      setSystemStats(realStats);
    } catch (error) {
      console.error('Error loading processes:', error);
      addLog('ERROR', 'Failed to load process information from MCP', 'ProcessManager');
    } finally {
      setLoading(false);
    }
  };

  const startAutoRefresh = () => {
    const interval = setInterval(() => {
      loadProcesses();
    }, 5000); // Refresh every 5 seconds

    setRefreshInterval(interval);
  };

  const toggleRefresh = () => {
    if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
      addLog('INFO', 'Auto-refresh disabled', 'ProcessManager');
    } else {
      startAutoRefresh();
      addLog('INFO', 'Auto-refresh enabled', 'ProcessManager');
    }
  };

  const sortProcesses = (processes: Process[]) => {
    return [...processes].sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortBy) {
        case 'cpu':
          aValue = a.cpuPercent;
          bValue = b.cpuPercent;
          break;
        case 'memory':
          aValue = a.memoryPercent;
          bValue = b.memoryPercent;
          break;
        case 'pid':
          aValue = a.pid;
          bValue = b.pid;
          break;
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const filteredAndSortedProcesses = sortProcesses(
    processes.filter(process =>
      process.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      process.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      process.pid.toString().includes(searchTerm)
    )
  );

  const formatMemory = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'running': return 'text-green-400';
      case 'sleeping': return 'text-blue-400';
      case 'stopped': return 'text-red-400';
      default: return 'text-yellow-400';
    }
  };

  const getLoadColor = (load: number): string => {
    if (load < 1) return 'text-green-400';
    if (load < 2) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#1e222d] border border-[#2a2e39] rounded-xl w-full max-w-7xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-[#2a2e39]">
          <div>
            <h2 className="text-xl font-black text-white uppercase italic tracking-tighter">
              Process <span className="text-purple-500">Management</span> Interface
            </h2>
            <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-1">
              System Process Controller
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-white transition-colors"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        {/* System Overview */}
        {systemStats && (
          <div className="p-6 border-b border-[#2a2e39] bg-[#1a1e27]">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-4">
                <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">CPU Usage</div>
                <div className="text-[16px] font-bold text-white">{systemStats.cpuPercent.toFixed(1)}%</div>
                <div className="w-full bg-[#0f1115] rounded-full h-2 mt-2">
                  <div
                    className="bg-gradient-to-r from-green-500 to-yellow-500 h-2 rounded-full"
                    style={{ width: `${systemStats.cpuPercent}%` }}
                  ></div>
                </div>
              </div>

              <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-4">
                <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Memory</div>
                <div className="text-[16px] font-bold text-white">{systemStats.memoryPercent.toFixed(1)}%</div>
                <div className="w-full bg-[#0f1115] rounded-full h-2 mt-2">
                  <div
                    className={`h-2 rounded-full ${systemStats.memoryPercent > 80 ? 'bg-red-500' :
                        systemStats.memoryPercent > 60 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                    style={{ width: `${systemStats.memoryPercent}%` }}
                  ></div>
                </div>
              </div>

              <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-4">
                <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Processes</div>
                <div className="text-[16px] font-bold text-white">{systemStats.processCount}</div>
                <div className="text-[9px] text-slate-500 mt-1">Active</div>
              </div>

              <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-4">
                <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Load Avg</div>
                <div className="text-[16px] font-bold text-white">
                  <span className={getLoadColor(systemStats.loadAverage[0])}>
                    {systemStats.loadAverage[0].toFixed(2)}
                  </span>
                </div>
                <div className="text-[9px] text-slate-500 mt-1">
                  1min / 5min / 15min
                </div>
              </div>

              <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-4">
                <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Uptime</div>
                <div className="text-[16px] font-bold text-white">
                  {formatUptime(systemStats.uptime)}
                </div>
                <div className="text-[9px] text-slate-500 mt-1">System running</div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Process List */}
            <div className="lg:w-2/3">
              <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                  <h3 className="text-[14px] font-black text-slate-500 uppercase tracking-widest">
                    Process List
                  </h3>

                  <div className="flex flex-wrap gap-2">
                    <input
                      type="text"
                      placeholder="Search processes..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="bg-[#1e222d] border border-[#2a2e39] rounded px-3 py-2 text-slate-300 text-[11px] focus:outline-none focus:border-purple-500"
                    />

                    <button
                      onClick={toggleRefresh}
                      className={`px-3 py-2 rounded text-[10px] font-black uppercase tracking-wider transition-all ${refreshInterval
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-slate-700/20 text-slate-400 border border-slate-700/30 hover:bg-slate-600/20 hover:text-white'
                        }`}
                    >
                      <i className={`fas ${refreshInterval ? 'fa-sync fa-spin' : 'fa-sync'} mr-2`}></i>
                      {refreshInterval ? 'Auto' : 'Manual'}
                    </button>

                    <button
                      onClick={loadProcesses}
                      disabled={loading}
                      className="px-3 py-2 bg-slate-700/20 hover:bg-slate-600/20 text-slate-400 hover:text-white border border-slate-700/30 hover:border-slate-600/30 rounded text-[10px] font-black uppercase tracking-wider transition-all disabled:opacity-50"
                    >
                      <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-refresh'} mr-2`}></i>
                      Refresh
                    </button>
                  </div>
                </div>

                {/* Table Headers */}
                <div className="grid grid-cols-12 gap-2 mb-3 px-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  <div className="col-span-1 cursor-pointer hover:text-white" onClick={() => { setSortBy('pid'); setSortOrder(sortBy === 'pid' && sortOrder === 'asc' ? 'desc' : 'asc'); }}>
                    PID {sortBy === 'pid' && <i className={`fas fa-sort-${sortOrder}`} />}
                  </div>
                  <div className="col-span-3 cursor-pointer hover:text-white" onClick={() => { setSortBy('name'); setSortOrder(sortBy === 'name' && sortOrder === 'asc' ? 'desc' : 'asc'); }}>
                    Name {sortBy === 'name' && <i className={`fas fa-sort-${sortOrder}`} />}
                  </div>
                  <div className="col-span-2">User</div>
                  <div className="col-span-2 cursor-pointer hover:text-white" onClick={() => { setSortBy('cpu'); setSortOrder(sortBy === 'cpu' && sortOrder === 'asc' ? 'desc' : 'asc'); }}>
                    CPU% {sortBy === 'cpu' && <i className={`fas fa-sort-${sortOrder}`} />}
                  </div>
                  <div className="col-span-2 cursor-pointer hover:text-white" onClick={() => { setSortBy('memory'); setSortOrder(sortBy === 'memory' && sortOrder === 'asc' ? 'desc' : 'asc'); }}>
                    Memory% {sortBy === 'memory' && <i className={`fas fa-sort-${sortOrder}`} />}
                  </div>
                  <div className="col-span-2">Status</div>
                </div>

                {/* Process List */}
                <div className="space-y-1 max-h-[400px] overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                    </div>
                  ) : (
                    filteredAndSortedProcesses.map(process => (
                      <div
                        key={process.pid}
                        className={`grid grid-cols-12 gap-2 p-3 rounded cursor-pointer transition-all border ${selectedProcess?.pid === process.pid
                            ? 'bg-purple-500/20 border-purple-500/50'
                            : 'bg-[#1e222d] border-[#2a2e39] hover:bg-[#2a2e39]'
                          }`}
                        onClick={() => setSelectedProcess(process)}
                      >
                        <div className="col-span-1 text-[11px] text-slate-300 font-mono">{process.pid}</div>
                        <div className="col-span-3 text-[11px] text-slate-300 truncate" title={process.name}>
                          {process.name}
                        </div>
                        <div className="col-span-2 text-[11px] text-slate-400">
                          {process.username || 'system'}
                        </div>
                        <div className="col-span-2 text-[11px] text-slate-300">
                          {process.cpuPercent.toFixed(1)}%
                        </div>
                        <div className="col-span-2 text-[11px] text-slate-300">
                          {process.memoryPercent.toFixed(1)}%
                        </div>
                        <div className="col-span-2 text-[11px] font-bold">
                          <span className={getStatusColor(process.status)}>
                            {process.status}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Process Details */}
            <div className="lg:w-1/3">
              {selectedProcess ? (
                <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-6 sticky top-6">
                  <h3 className="text-[14px] font-black text-slate-500 uppercase tracking-widest mb-4">
                    Process Details
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Process Name</div>
                      <div className="text-[12px] font-bold text-white">{selectedProcess.name}</div>
                    </div>

                    <div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Process ID</div>
                      <div className="text-[12px] font-mono text-slate-300">{selectedProcess.pid}</div>
                    </div>

                    <div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Status</div>
                      <div className={`text-[12px] font-bold ${getStatusColor(selectedProcess.status)}`}>
                        {selectedProcess.status}
                      </div>
                    </div>

                    <div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">CPU Usage</div>
                      <div className="text-[12px] text-slate-300">{selectedProcess.cpuPercent.toFixed(2)}%</div>
                    </div>

                    <div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Memory Usage</div>
                      <div className="text-[12px] text-slate-300">
                        {selectedProcess.memoryPercent.toFixed(2)}% ({formatMemory(selectedProcess.memoryRSS)})
                      </div>
                    </div>

                    <div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">User</div>
                      <div className="text-[12px] text-slate-300">{selectedProcess.username || 'system'}</div>
                    </div>

                    <div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Created</div>
                      <div className="text-[12px] text-slate-300">
                        {new Date(selectedProcess.createTime).toLocaleString()}
                      </div>
                    </div>

                    {selectedProcess.cmdline && selectedProcess.cmdline.length > 0 && (
                      <div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Command Line</div>
                        <div className="text-[10px] text-slate-300 bg-[#0f1115] rounded p-2 font-mono break-words">
                          {selectedProcess.cmdline.join(' ')}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-6 pt-6 border-t border-[#2a2e39] space-y-2">
                    <button onClick={async () => {
                      addLog('WARN', `Attempting to terminate process ${selectedProcess.pid}`);
                      try {
                        await backendService.executeMcpTool('desktop-commander', 'execute_command', { command: `kill -9 ${selectedProcess.pid}` });
                        addLog('INFO', `Successfully killed process ${selectedProcess.pid}`);
                        loadProcesses();
                      } catch (e) {
                        addLog('ERROR', `Failed to kill process: ${e}`);
                      }
                    }} className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-black py-2 px-4 rounded text-[10px] uppercase tracking-wider transition-all">
                      <i className="fas fa-power-off mr-2"></i>
                      Terminate Process
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-12 text-center">
                  <i className="fas fa-microchip text-4xl text-slate-600 mb-4 opacity-20"></i>
                  <h3 className="text-[14px] font-black text-slate-500 uppercase tracking-widest mb-2">
                    No Process Selected
                  </h3>
                  <p className="text-[11px] text-slate-600">
                    Click on a process to view detailed information
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#2a2e39] bg-[#1a1e27] rounded-b-xl">
          <div className="flex justify-between items-center text-[10px] text-slate-500 uppercase tracking-widest">
            <div className="flex items-center gap-4">
              <span>Total Processes: {processes.length}</span>
              <span>Filtered: {filteredAndSortedProcesses.length}</span>
              <span>Sort: {sortBy.toUpperCase()} ({sortOrder})</span>
            </div>
            <div className="flex items-center gap-2">
              <i className={`fas fa-circle ${loading ? 'text-yellow-500' : 'text-green-500'}`}></i>
              <span>{loading ? 'Loading...' : 'Monitoring Active'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessManagementPanel;